<template>
  <v-dialog
    v-model="dialog"
    max-width="1200px"
    scrollable
  >
    <v-card>
      <v-card-title class="d-block">
        <div class="text-h5">
          Provisioning QR
        </div>
        <div class="provisioning-qr-subtitle mt-1">
          Generate an Android Enterprise kiosk enrollment QR for
          {{ companyName || 'the selected company' }}.
        </div>
      </v-card-title>

      <v-card-text>
        <v-alert
          dense
          text
          border="left"
          :type="statusType"
          class="mb-2"
        >
          {{ statusMessage }}
        </v-alert>

        <v-progress-linear
          v-if="isPreparingApk"
          :value="downloadProgress"
          :indeterminate="isCalculatingChecksum || !isDownloadProgressKnown"
          height="6"
          rounded
          color="primary"
          class="mb-4"
        />
        <div
          v-else
          class="mb-4"
        />

        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model.trim="apkDownloadUrl"
              outlined
              dense
              label="APK Download URL"
              placeholder="https://example.com/mydevice.apk"
              hint="Auto-filled with the latest APK. Edit to install from a different URL."
              persistent-hint
              class="mb-4"
              :loading="isPreparingApk"
              :disabled="isPreparingApk"
              :error-messages="apkUrlError"
              @change="onApkUrlChanged"
            />

            <v-combobox
              v-model="deviceImei"
              outlined
              dense
              label="Vehicle Number"
              :items="vehicleNumberOptions"
              item-text="name"
              item-value="name"
              :search-input.sync="vehicleSearchInput"
              :loading="isLoadingCompanyDevices"
              :filter="filterVehicleNumberOption"
              hide-no-data
              clearable
              hint="Use uppercase letters first, followed by numbers only. Example: T7518."
              persistent-hint
              :error-messages="vehicleNumberError"
              class="mb-4"
              @change="onVehicleNumberChanged"
              @blur="applyVehicleMacForCurrentInput"
            />

            <v-text-field
              v-model.trim="deviceMacAddress"
              outlined
              dense
              label="MAC Address"
              :hint="deviceMacAddressHint"
              persistent-hint
              :readonly="!!selectedVehicleMacAddress"
              :append-icon="selectedVehicleMacAddress ? '' : 'mdi-refresh'"
              @change="onDeviceMacAddressChanged"
              @click:append="regenerateDeviceMacAddress"
            />

            <v-divider class="my-4" />

            <div class="wifi-section-heading mb-3">
              <div>
                <div class="text-subtitle-2 font-weight-medium">
                  Optional Wi-Fi
                </div>
                <div
                  class="text-caption"
                  :class="wifiConfigLoadError ? 'error--text' : 'grey--text text--darken-1'"
                >
                  {{ wifiConfigStatusMessage }}
                </div>
              </div>
              <v-btn
                icon
                small
                :loading="isLoadingWifiConfig"
                @click="reloadCompanyWifiConfig"
              >
                <v-icon small>
                  mdi-refresh
                </v-icon>
              </v-btn>
            </div>

            <v-checkbox
              v-model="wifiDisabled"
              dense
              hide-details
              class="mt-0 mb-3"
              label="Disable WiFi"
            />

            <v-select
              v-if="hasMultipleWifiConfigs"
              v-model="selectedWifiConfigKey"
              outlined
              dense
              label="WiFi SSID"
              :items="wifiConfigSelectItems"
              item-text="label"
              item-value="value"
              hint="Choose which company Wi-Fi config to include in the QR payload."
              persistent-hint
              class="mb-4"
              :disabled="wifiDisabled"
              @change="selectWifiConfigByKey"
            />

            <v-text-field
              v-else
              v-model.trim="wifiSsid"
              outlined
              dense
              label="WiFi SSID"
              hint="Leave blank to omit Wi-Fi settings from the QR payload."
              persistent-hint
              class="mb-4"
              :disabled="wifiDisabled"
            />

            <v-combobox
              v-model="wifiSecurityType"
              outlined
              dense
              label="WiFi Security Type"
              :items="wifiSecurityTypes"
              hint="Included in the QR only when Wi-Fi is enabled and an SSID is provided."
              persistent-hint
              class="mb-4"
              :disabled="wifiDisabled"
            />

            <v-text-field
              v-model="wifiPassword"
              outlined
              dense
              label="WiFi Password"
              :type="showWifiPassword ? 'text' : 'password'"
              :append-icon="showWifiPassword ? 'mdi-eye-off' : 'mdi-eye'"
              hint="Only included when Wi-Fi is enabled and SSID is filled."
              persistent-hint
              :disabled="wifiDisabled"
              @click:append="toggleWifiPassword"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="provisioning-qr-preview-col"
          >
            <div
              ref="qrWrapper"
              class="provisioning-qr-preview"
              :class="{ 'provisioning-qr-preview--empty': !canGenerate }"
            >
              <div
                v-if="canGenerate"
                class="provisioning-qr-frame"
              >
                <qrcode-vue
                  :value="qrValue"
                  :size="$vuetify.breakpoint.mdAndDown ? 360 : 460"
                  level="L"
                  render-as="canvas"
                />
              </div>
              <div
                v-else
                class="text-body-2 text-center grey--text text--darken-1"
              >
                The QR code appears once the APK URL, checksum, and vehicle number are ready.
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn
          text
          small
          :loading="isPreparingApk"
          @click="reloadLatestApk"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-refresh
          </v-icon>
          Latest APK
        </v-btn>
        <v-spacer />
        <v-btn
          text
          :disabled="!canGenerate"
          @click="copyJson"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-content-copy
          </v-icon>
          Copy JSON
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!canGenerate"
          @click="downloadQrCode"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-download
          </v-icon>
          Download QR
        </v-btn>
        <v-btn
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import QrcodeVue from 'qrcode.vue'
  import formatter from '@/util/formatter'
  import { uuidv7 } from 'uuidv7'
  import { encryptCompanyCode } from '@/utils/companyCodeCipher'

  const toastOptions = { position: 'topRight' }

  const generateUuidV7MacAddress = () => {
    const uuidHex = uuidv7().replace(/-/g, '')
    const macBytes = uuidHex.slice(0, 12).match(/.{1,2}/g) || []

    if (macBytes.length) {
      const firstByte = parseInt(macBytes[0], 16)
      macBytes[0] = ((firstByte | 0x02) & 0xfe).toString(16).padStart(2, '0')
    }

    return macBytes.join(':').toUpperCase()
  }

  const normalizeVehicleKey = (value) =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')

  const normalizeMacAddressValue = (value) =>
    String(value || '').trim().toUpperCase()

  const getDeviceNameValue = (device) => String(device?.name || '').trim()

  const getDeviceMacAddressValue = (device) =>
    normalizeMacAddressValue(
      device?.macAddress ||
      device?.deviceMacAddress ||
      device?.deviceId ||
      device?.id ||
      '',
    )

  const getDeviceLatestTimestamp = (device) => {
    const candidates = [
      device?.lastConnected,
      device?.lastConnectedAt,
      device?.updatedAt,
      device?.modifiedAt,
      device?.createdAt,
      device?.created,
    ]

    for (const value of candidates) {
      if (!value) {
        continue
      }

      // Parse as UTC: the API's timestamps are naive, and mixing a naive read
      // with an offset-bearing one would order the candidates wrongly.
      const timestamp = formatter.toDate(value)
      if (timestamp) {
        return timestamp.getTime()
      }
    }

    return 0
  }

  // The kiosk APK admin receiver is fixed for MyDevice provisioning, so it is
  // baked into the QR payload and never surfaced in the UI.
  const ADMIN_COMPONENT_NAME =
    'au.com.softclient.mydevices/.service.device.MyDeviceAdminReceiver'
  const MYDEVICE_PACKAGE_ID = 'au.com.softclient.mydevices'
  const MYDEVICE_APK_PATTERN = /(?:^|[/\\_\s-])mydevice(?:[/\\_\s.-]|$)/i
  const SHA_256_BASE64_URL_PATTERN = /^[A-Za-z0-9_-]{43}$/
  const VEHICLE_NUMBER_PATTERN = /^[A-Z]+[0-9]*$/

  const PROVISIONING_KEYS = {
    adminComponentName: 'android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME',
    downloadLocation: 'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION',
    checksum: 'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM',
    wifiSsid: 'android.app.extra.PROVISIONING_WIFI_SSID',
    wifiPassword: 'android.app.extra.PROVISIONING_WIFI_PASSWORD',
    wifiSecurityType: 'android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE',
    adminExtrasBundle: 'android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE',
  }

  const WIFI_SECURITY_TYPES = [
    'WPA',
    'WPA2',
    'WPA3',
    'WPA/WPA2 PSK',
    'WPA2/WPA3 PSK',
    'SAE',
    'OWE',
    'EAP',
    'EAP-TLS',
    'EAP-TTLS',
    'PEAP',
    'WEP',
    'NONE',
  ]

  const DEFAULT_WIFI_SECURITY_TYPE = 'WPA'

  const getNestedStringValue = (candidate) => {
    if (typeof candidate === 'string') {
      return candidate.trim()
    }

    if (candidate && typeof candidate === 'object') {
      const nestedCandidates = [
        candidate.path,
        candidate.filePath,
        candidate.fileName,
        candidate.name,
        candidate.url,
      ]

      return nestedCandidates.find(value => typeof value === 'string' && value.trim())
    }

    return ''
  }

  const isValidHttpUrl = (value) => {
    try {
      const parsedUrl = new URL(String(value || '').trim())
      return ['http:', 'https:'].includes(parsedUrl.protocol)
    } catch (error) {
      return false
    }
  }

  const getFileNameFromUrl = (value) => {
    const cleaned = String(value || '').trim().split(/[?#]/)[0]
    return cleaned.split(/[\\/]/).filter(Boolean).pop() || ''
  }

  const readFileAsArrayBuffer = (file) => {
    if (typeof file.arrayBuffer === 'function') {
      return file.arrayBuffer()
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsArrayBuffer(file)
    })
  }

  const normalizeChecksumValue = (value) => {
    if (typeof value !== 'string') {
      return value
    }

    return value.trim().replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
  }

  const arrayBufferToBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer)
    const chunkSize = 0x8000
    let binary = ''

    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize)
      binary += String.fromCharCode.apply(null, chunk)
    }

    return normalizeChecksumValue(window.btoa(binary))
  }

  const copyTextToClipboard = async (value) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value)
      return
    }

    const fallback = document.createElement('textarea')
    fallback.value = value
    fallback.setAttribute('readonly', '')
    fallback.style.position = 'absolute'
    fallback.style.left = '-9999px'
    document.body.appendChild(fallback)
    fallback.select()
    document.execCommand('copy')
    document.body.removeChild(fallback)
  }

  const normalizeConfigSearchLabel = (value) =>
    String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

  const normalizeConfigSearchPath = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\\/g, '/')
      .replace(/^\/+|\/+$/g, '')
      .replace(/\/+/g, '/')

  const scoreDownloadsConfig = (config) => {
    const normalizedName = normalizeConfigSearchLabel(config?.name)
    const normalizedPath = normalizeConfigSearchPath(config?.devicePath)
    const lastPathSegment = normalizedPath.split('/').filter(Boolean).pop() || ''

    let score = -1

    if (['download', 'downloads'].includes(lastPathSegment)) {
      score = Math.max(score, 100)
    }

    if (
      normalizedPath === 'download' ||
      normalizedPath === 'downloads' ||
      normalizedPath.endsWith('/download') ||
      normalizedPath.endsWith('/downloads')
    ) {
      score = Math.max(score, 95)
    }

    if (
      ['download', 'downloads', 'script download', 'script downloads', 'scripts download', 'scripts downloads']
        .includes(normalizedName)
    ) {
      score = Math.max(score, 90)
    }

    if (/\bdownloads?\b/.test(normalizedName) && /\bscripts?\b/.test(normalizedName)) {
      score = Math.max(score, 85)
    }

    if (/\bdownloads?\b/.test(normalizedName)) {
      score = Math.max(score, 75)
    }

    if (/\bdownloads?\b/.test(normalizedPath)) {
      score = Math.max(score, 70)
    }

    return score
  }

  const parseExistingApkVersionParts = (value) => {
    const versionMatch = String(value || '').match(/(?:^|[^a-z0-9])v?(\d+(?:\.\d+){1,3})(?:[_\s-]+(\d+))?/i)
    if (!versionMatch) {
      return []
    }

    return [
      ...versionMatch[1].split('.').map(part => Number(part) || 0),
      Number(versionMatch[2]) || 0,
    ]
  }

  const findLatestNumericToken = (value) => {
    const matches = String(value || '').match(/\d{10,}/g) || []
    return matches.sort((left, right) => {
      if (left.length !== right.length) {
        return right.length - left.length
      }
      return right.localeCompare(left)
    })[0] || ''
  }

  const compareNumberParts = (leftParts, rightParts) => {
    const length = Math.max(leftParts.length, rightParts.length)
    for (let index = 0; index < length; index += 1) {
      const diff = (leftParts[index] || 0) - (rightParts[index] || 0)
      if (diff !== 0) {
        return diff
      }
    }
    return 0
  }

  const compareLatestExistingApkOptions = (left, right) => {
    const leftText = [left?.name, left?.filePathLabel, left?.downloadUrl].filter(Boolean).join(' ')
    const rightText = [right?.name, right?.filePathLabel, right?.downloadUrl].filter(Boolean).join(' ')
    const leftToken = findLatestNumericToken(leftText)
    const rightToken = findLatestNumericToken(rightText)

    if (leftToken || rightToken) {
      if (leftToken.length !== rightToken.length) {
        return leftToken.length - rightToken.length
      }
      const tokenComparison = leftToken.localeCompare(rightToken)
      if (tokenComparison !== 0) {
        return tokenComparison
      }
    }

    const versionComparison = compareNumberParts(
      parseExistingApkVersionParts(leftText),
      parseExistingApkVersionParts(rightText),
    )
    if (versionComparison !== 0) {
      return versionComparison
    }

    return String(left?.name || '').localeCompare(String(right?.name || ''))
  }

  export default {
    name: 'CompanyProvisioningQrDialog',

    components: {
      QrcodeVue,
    },

    props: {
      value: {
        type: Boolean,
        default: false,
      },
      company: {
        type: Object,
        default: () => ({}),
      },
    },

    data: () => ({
      dialog: false,
      apkDownloadUrl: '',
      apkChecksum: '',
      apkName: '',
      apkError: '',
      checksumUrl: '',
      isPreparingApk: false,
      isCalculatingChecksum: false,
      downloadProgress: 0,
      isDownloadProgressKnown: false,
      apkRequestToken: 0,
      deviceImei: '',
      deviceMacAddress: generateUuidV7MacAddress(),
      vehicleSearchInput: '',
      companyDeviceRows: [],
      isLoadingCompanyDevices: false,
      companyDeviceLoadError: '',
      companyDeviceRequestToken: 0,
      selectedExistingVehicleName: '',
      generatedVehicleName: '',
      lastRecordedQrSignature: '',
      qrProvisioningId: '',
      wifiDisabled: false,
      wifiSsid: '',
      wifiPassword: '',
      wifiSecurityType: DEFAULT_WIFI_SECURITY_TYPE,
      wifiConfigLoadError: '',
      wifiConfigLoadedCompanyId: 0,
      wifiConfigOptions: [],
      wifiConfigRequestToken: 0,
      isLoadingWifiConfig: false,
      selectedWifiConfigKey: '',
      showWifiPassword: false,
    }),

    computed: {
      companyId () {
        return this.company?.id || 0
      },
      companyCode () {
        return String(this.company?.code || '').trim()
      },
      companyName () {
        return String(this.company?.name || '').trim()
      },
      baseConfigCompanyId () {
        return this.$store?.state?.configs?.baseConfigCompanyId || 1
      },
      vehicleNumberOptions () {
        const latestByVehicle = {}

        this.companyDeviceRows.forEach((device, index) => {
          const name = getDeviceNameValue(device)
          const macAddress = getDeviceMacAddressValue(device)
          const key = normalizeVehicleKey(name)

          if (!key || !macAddress) {
            return
          }

          const timestamp = getDeviceLatestTimestamp(device)
          const existing = latestByVehicle[key]

          if (
            !existing ||
            timestamp > existing.timestamp ||
            (timestamp === existing.timestamp && index > existing.index)
          ) {
            latestByVehicle[key] = { name, macAddress, timestamp, index }
          }
        })

        return Object.values(latestByVehicle).sort((left, right) =>
          left.name.localeCompare(right.name),
        )
      },
      vehicleMacByName () {
        return this.vehicleNumberOptions.reduce((lookup, option) => {
          lookup[normalizeVehicleKey(option.name)] = option.macAddress
          return lookup
        }, {})
      },
      selectedVehicleMacAddress () {
        return this.vehicleMacByName[normalizeVehicleKey(this.deviceImei)] || ''
      },
      deviceMacAddressHint () {
        if (this.selectedVehicleMacAddress) {
          return 'Reused from the latest matching vehicle in the selected company.'
        }

        if (this.isLoadingCompanyDevices) {
          return 'Loading company device list for vehicle matching...'
        }

        if (this.companyDeviceLoadError) {
          return this.companyDeviceLoadError
        }

        return 'Auto-generated for new vehicles from a date-based UUID v7 value.'
      },
      apkUrlError () {
        const url = String(this.apkDownloadUrl || '').trim()
        if (url && !isValidHttpUrl(url)) {
          return ['Enter a valid http or https URL.']
        }

        return []
      },
      statusType () {
        if (this.apkError) {
          return 'error'
        }

        if (this.isPreparingApk) {
          return 'info'
        }

        return this.apkDownloadUrl && this.apkChecksum ? 'success' : 'info'
      },
      statusMessage () {
        if (this.apkError) {
          return this.apkError
        }

        if (this.isPreparingApk) {
          if (this.isCalculatingChecksum) {
            return 'Calculating the SHA-256 checksum locally...'
          }

          return this.isDownloadProgressKnown && this.downloadProgress > 0
            ? `Downloading the APK for checksum generation... ${this.downloadProgress}%`
            : 'Downloading the APK for checksum generation...'
        }

        if (this.apkDownloadUrl && this.apkChecksum) {
          return `Checksum ready for ${this.apkName || 'the selected APK'}.`
        }

        return 'No APK is loaded yet.'
      },
      vehicleNumber () {
        return this.normalizeVehicleInput(this.deviceImei)
      },
      vehicleNumberError () {
        if (!this.vehicleNumber) {
          return []
        }

        if (!VEHICLE_NUMBER_PATTERN.test(this.vehicleNumber)) {
          return [
            'Use uppercase letters first, then numbers only. No spaces, symbols, lowercase letters, or letters after numbers.',
          ]
        }

        return []
      },
      macAddress () {
        return String(this.deviceMacAddress || '').trim()
      },
      wifiSecurityTypes () {
        return WIFI_SECURITY_TYPES
      },
      hasWifiConfiguration () {
        return !this.wifiDisabled && String(this.wifiSsid || '').trim().length > 0
      },
      hasMultipleWifiConfigs () {
        return this.wifiConfigOptions.length > 1
      },
      wifiConfigSelectItems () {
        return this.wifiConfigOptions.map((wifiConfig) => {
          const essid = String(wifiConfig?.essid || '').trim() || 'Unnamed Wi-Fi'
          const suffixParts = []

          if (wifiConfig?.active === true) {
            suffixParts.push('active')
          }

          if (wifiConfig?.encryption) {
            suffixParts.push(wifiConfig.encryption)
          }

          return {
            label: suffixParts.length ? `${essid} (${suffixParts.join(', ')})` : essid,
            value: wifiConfig.selectionKey,
          }
        })
      },
      wifiConfigStatusMessage () {
        if (this.wifiDisabled) {
          return 'Wi-Fi will be omitted from this QR payload.'
        }

        if (this.isLoadingWifiConfig) {
          return 'Loading company Wi-Fi config...'
        }

        if (this.wifiConfigLoadError) {
          return this.wifiConfigLoadError
        }

        if (this.hasMultipleWifiConfigs) {
          return 'Choose a company Wi-Fi config for this QR payload.'
        }

        if (this.wifiConfigLoadedCompanyId && this.hasWifiConfiguration) {
          return 'Loaded from the selected company Wi-Fi config.'
        }

        return 'No company Wi-Fi config is loaded.'
      },
      qrProvisioningSignature () {
        if (!this.companyId || !this.vehicleNumber || !this.macAddress) {
          return ''
        }

        return `${this.companyId}|${normalizeVehicleKey(this.vehicleNumber)}|${this.macAddress.toUpperCase()}`
      },
      canGenerate () {
        return !!(
          this.apkDownloadUrl &&
          this.apkChecksum &&
          !this.apkUrlError.length &&
          this.vehicleNumber &&
          !this.vehicleNumberError.length &&
          this.macAddress &&
          this.qrProvisioningId &&
          this.lastRecordedQrSignature === this.qrProvisioningSignature
        )
      },
      provisioningPayload () {
        if (!this.canGenerate) {
          return null
        }

        const payload = {
          [PROVISIONING_KEYS.adminComponentName]: ADMIN_COMPONENT_NAME,
          [PROVISIONING_KEYS.downloadLocation]: this.apkDownloadUrl,
          [PROVISIONING_KEYS.checksum]: normalizeChecksumValue(this.apkChecksum),
        }

        const adminExtrasBundle = {}

        if (this.companyCode) {
          adminExtrasBundle.companyCode = encryptCompanyCode(this.companyCode)
        }

        if (this.vehicleNumber) {
          adminExtrasBundle.vehicleNumber = this.vehicleNumber
        }

        if (this.macAddress) {
          adminExtrasBundle.macAddress = this.macAddress
        }

        if (this.qrProvisioningId) {
          adminExtrasBundle.qrId = this.qrProvisioningId
        }

        if (Object.keys(adminExtrasBundle).length) {
          payload[PROVISIONING_KEYS.adminExtrasBundle] = adminExtrasBundle
        }

        if (this.hasWifiConfiguration) {
          payload[PROVISIONING_KEYS.wifiSsid] = String(this.wifiSsid || '').trim()
          payload[PROVISIONING_KEYS.wifiPassword] = this.wifiPassword || ''
          payload[PROVISIONING_KEYS.wifiSecurityType] =
            this.wifiSecurityType || DEFAULT_WIFI_SECURITY_TYPE
        }

        return payload
      },
      formattedJson () {
        return this.provisioningPayload ? JSON.stringify(this.provisioningPayload, null, 2) : ''
      },
      qrValue () {
        return this.formattedJson
      },
    },

    watch: {
      value: {
        immediate: true,
        handler (val) {
          this.dialog = val
        },
      },
      dialog (val) {
        this.$emit('input', val)
        if (val) {
          this.prepareLatestApk()
          this.loadCompanyDevices()
          this.loadCompanyWifiConfig()
        }
      },
      companyId (newCompanyId) {
        this.clearQrProvisioningRecord()
        this.clearWifiConfigFields()
        if (this.dialog && newCompanyId) {
          this.prepareLatestApk()
          this.loadCompanyDevices()
          this.loadCompanyWifiConfig()
        }
      },
    },

    methods: {
      buildDownloadUrlFromPath (filePath) {
        const resourceBaseUrl = this.$http.defaults.baseURL.replace('api/', 'Resources/')
        const normalizedPath = String(filePath || '')
          .replace(/\\/g, '/')
          .replace(/^\/+/, '')
          .replace(/^resources\/+/i, '')

        return new URL(normalizedPath, resourceBaseUrl).toString()
      },
      resolveUploadFilePath (file, fallbackName) {
        const candidates = [
          file?.filePath,
          file?.path,
          file?.downloadUrl,
          file?.url,
          file?.name,
        ]

        for (const candidate of candidates) {
          const stringValue = getNestedStringValue(candidate)
          if (!stringValue) {
            continue
          }

          if (/^https?:\/\//i.test(stringValue) || /^download\//i.test(stringValue)) {
            return stringValue
          }

          if (/\.apk$/i.test(stringValue)) {
            return `Download/${stringValue.split(/[\\/]/).pop()}`
          }
        }

        return `Download/${fallbackName}`
      },
      normalizeExistingApkOption (file) {
        const fileName = getNestedStringValue(file) || 'Unnamed APK'
        const resolvedPathOrUrl = this.resolveUploadFilePath(file, fileName)
        const apkSearchSource = [
          fileName,
          resolvedPathOrUrl,
          file?.filePath,
          file?.path,
          file?.packageName,
        ]
          .filter(Boolean)
          .join(' ')

        if (!/\.apk(\b|$)/i.test(apkSearchSource)) {
          return null
        }

        if (
          !apkSearchSource.toLowerCase().includes(MYDEVICE_PACKAGE_ID) &&
          !MYDEVICE_APK_PATTERN.test(apkSearchSource)
        ) {
          return null
        }

        const downloadUrl = /^https?:\/\//i.test(resolvedPathOrUrl)
          ? resolvedPathOrUrl
          : this.buildDownloadUrlFromPath(resolvedPathOrUrl)

        return {
          id: file?.id || file?.deviceConfigFilePathId || downloadUrl,
          name: fileName,
          checksum: normalizeChecksumValue(file?.sha256 || ''),
          filePathLabel: /^https?:\/\//i.test(resolvedPathOrUrl)
            ? resolvedPathOrUrl
            : resolvedPathOrUrl.replace(/^\/+/, ''),
          downloadUrl,
        }
      },
      getDownloadsSearchCompanyIds () {
        return [...new Set([this.companyId, this.baseConfigCompanyId].filter(Boolean))]
      },
      async getDeviceConfigsByCompany (companyId) {
        const response = await this.$http.get('DeviceConfig/GetDeviceConfigsByCompany', {
          params: { CompanyId: companyId },
        })

        return Array.isArray(response.data) ? response.data : []
      },
      async findDownloadsConfigs () {
        const searchCompanyIds = this.getDownloadsSearchCompanyIds()
        const configResults = await Promise.all(
          searchCompanyIds.map(async (companyId) => ({
            companyId,
            configs: await this.getDeviceConfigsByCompany(companyId),
          })),
        )

        const matches = []

        configResults.forEach(({ companyId, configs }, companyIndex) => {
          configs.forEach((config) => {
            const score = scoreDownloadsConfig(config)
            if (score < 0) {
              return
            }

            matches.push({ companyId, companyIndex, config, score })
          })
        })

        matches.sort((left, right) => {
          if (right.score !== left.score) {
            return right.score - left.score
          }

          if (left.companyIndex !== right.companyIndex) {
            return left.companyIndex - right.companyIndex
          }

          return String(left.config?.name || '').localeCompare(String(right.config?.name || ''))
        })

        return matches
      },
      async loadLatestApkOption () {
        const downloadsConfigs = await this.findDownloadsConfigs()

        if (!downloadsConfigs.length) {
          throw new Error('Downloads script config was not found for this company.')
        }

        const files = (
          await Promise.all(
            downloadsConfigs.map(async ({ config }) => {
              const response = await this.$http.get('DeviceConfig/GetFilePathsByDeviceConfigs', {
                params: { DeviceConfigId: config.id },
              })

              return Array.isArray(response.data) ? response.data : []
            }),
          )
        ).flat()

        const seenOptions = new Set()
        const apkOptions = files
          .map(file => this.normalizeExistingApkOption(file))
          .filter((option) => {
            if (!option || seenOptions.has(option.id)) {
              return false
            }

            seenOptions.add(option.id)
            return true
          })

        return apkOptions.reduce(
          (latest, item) =>
            !latest || compareLatestExistingApkOptions(item, latest) > 0 ? item : latest,
          null,
        )
      },
      async downloadApkBlob (downloadUrl) {
        this.isDownloadProgressKnown = false
        this.downloadProgress = 0

        const response = await this.$http(downloadUrl, {
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.isDownloadProgressKnown = true
              this.downloadProgress = Math.min(
                100,
                Math.round((progressEvent.loaded * 100) / progressEvent.total),
              )
            }
          },
        })

        if (!this.isDownloadProgressKnown) {
          this.downloadProgress = 100
        }

        return response.data
      },
      async generateChecksumFromApk (file) {
        if (!window.crypto || !window.crypto.subtle) {
          throw new Error('This browser cannot calculate SHA-256 checksums securely.')
        }

        // Hash the APK in the browser only. The file never leaves local memory.
        const fileBuffer = await readFileAsArrayBuffer(file)
        const digest = await window.crypto.subtle.digest('SHA-256', fileBuffer)
        return arrayBufferToBase64Url(digest)
      },
      async resolveChecksumForUrl (url, requestToken) {
        const apkBlob = await this.downloadApkBlob(url)

        if (requestToken !== this.apkRequestToken) {
          return false
        }

        this.isCalculatingChecksum = true
        try {
          this.apkChecksum = await this.generateChecksumFromApk(apkBlob)
        } finally {
          if (requestToken === this.apkRequestToken) {
            this.isCalculatingChecksum = false
          }
        }
        this.checksumUrl = url
        return true
      },
      async prepareLatestApk () {
        const requestToken = this.apkRequestToken + 1
        this.apkRequestToken = requestToken
        this.apkError = ''
        this.apkDownloadUrl = ''
        this.apkChecksum = ''
        this.apkName = ''
        this.checksumUrl = ''

        if (!this.companyId) {
          this.apkError = 'Select a company to load the latest APK.'
          return
        }

        this.isPreparingApk = true

        try {
          const latestApk = await this.loadLatestApkOption()

          if (requestToken !== this.apkRequestToken) {
            return
          }

          if (!latestApk) {
            this.apkError = 'No MyDevice APK files were found in Scripts Downloads for this company.'
            return
          }

          this.apkDownloadUrl = latestApk.downloadUrl
          this.apkName = latestApk.name

          // Reuse the checksum returned by the API when present; only download
          // and hash the APK locally when no usable checksum is available.
          if (latestApk.checksum && SHA_256_BASE64_URL_PATTERN.test(latestApk.checksum)) {
            this.apkChecksum = latestApk.checksum
            this.checksumUrl = latestApk.downloadUrl
          } else {
            await this.resolveChecksumForUrl(latestApk.downloadUrl, requestToken)
          }
        } catch (error) {
          if (requestToken !== this.apkRequestToken) {
            return
          }

          this.apkChecksum = ''
          this.checksumUrl = ''
          this.apkError = this.describeApkDownloadError(error)
        } finally {
          if (requestToken === this.apkRequestToken) {
            this.isPreparingApk = false
            this.isCalculatingChecksum = false
            this.isDownloadProgressKnown = false
            this.downloadProgress = 0
          }
        }
      },
      reloadLatestApk () {
        this.prepareLatestApk()
      },
      normalizeWifiConfigOption (wifiConfig, index) {
        return {
          ...wifiConfig,
          selectionKey: String(
            wifiConfig?.id ||
            wifiConfig?.wifiConfigId ||
            `${wifiConfig?.referenceId || this.companyId || 'company'}-${wifiConfig?.essid || 'wifi'}-${index}`,
          ),
        }
      },
      normalizeWifiConfigResponse (responseData) {
        const data = responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : responseData

        const rawItems = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : data && typeof data === 'object'
                ? [data]
                : []

        return rawItems
          .filter(item => item && typeof item === 'object')
          .map((wifiConfig, index) => this.normalizeWifiConfigOption(wifiConfig, index))
          .sort((left, right) => {
            if (left.active !== right.active) {
              return left.active ? -1 : 1
            }

            return String(left.essid || '').localeCompare(String(right.essid || ''))
          })
      },
      normalizeWifiSecurityType (value) {
        const normalizedValue = String(value || '').trim()
        const lowerValue = normalizedValue.toLowerCase()

        if (!normalizedValue) {
          return DEFAULT_WIFI_SECURITY_TYPE
        }

        if (lowerValue === 'none' || lowerValue === 'open') {
          return 'NONE'
        }

        if (lowerValue.includes('wep')) {
          return 'WEP'
        }

        if (
          lowerValue.includes('wpa') ||
          lowerValue.includes('psk') ||
          lowerValue.includes('personal')
        ) {
          return 'WPA'
        }

        return normalizedValue
      },
      clearWifiConfigFields () {
        this.wifiSsid = ''
        this.wifiPassword = ''
        this.wifiSecurityType = DEFAULT_WIFI_SECURITY_TYPE
        this.wifiConfigLoadedCompanyId = 0
        this.wifiConfigOptions = []
        this.selectedWifiConfigKey = ''
      },
      applyWifiConfig (wifiConfig) {
        this.wifiSsid = String(wifiConfig?.essid || '').trim()
        this.wifiPassword = wifiConfig?.password || ''
        this.wifiSecurityType = this.normalizeWifiSecurityType(wifiConfig?.encryption)
        this.wifiConfigLoadedCompanyId = this.companyId
        this.selectedWifiConfigKey = wifiConfig?.selectionKey || ''
      },
      selectWifiConfigByKey (selectionKey) {
        const wifiConfig = this.wifiConfigOptions.find(
          option => option.selectionKey === selectionKey,
        )

        if (wifiConfig) {
          this.applyWifiConfig(wifiConfig)
        }
      },
      async loadCompanyWifiConfig () {
        const companyId = this.companyId
        const requestToken = this.wifiConfigRequestToken + 1
        this.wifiConfigRequestToken = requestToken
        this.wifiConfigLoadError = ''

        if (!companyId) {
          this.clearWifiConfigFields()
          this.wifiConfigLoadError = 'Select a company to load Wi-Fi config.'
          return
        }

        this.isLoadingWifiConfig = true

        try {
          const response = await this.$http.get('WifiConfig/GetWifiConfigByCompany', {
            params: { companyId },
          })

          if (requestToken !== this.wifiConfigRequestToken) {
            return
          }

          const wifiConfigs = this.normalizeWifiConfigResponse(response?.data)
          this.wifiConfigOptions = wifiConfigs

          if (!wifiConfigs.length || !wifiConfigs[0]?.essid) {
            this.clearWifiConfigFields()
            this.wifiConfigLoadError = 'No Wi-Fi config found for the selected company.'
            return
          }

          this.applyWifiConfig(wifiConfigs[0])
        } catch (error) {
          if (requestToken !== this.wifiConfigRequestToken) {
            return
          }

          this.clearWifiConfigFields()
          this.wifiConfigLoadError =
            error?.response?.data?.message ||
            'We could not load Wi-Fi config for the selected company.'
          console.error('[CompanyProvisioningQrDialog] Failed to load company Wi-Fi config', {
            companyId,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
        } finally {
          if (requestToken === this.wifiConfigRequestToken) {
            this.isLoadingWifiConfig = false
          }
        }
      },
      reloadCompanyWifiConfig () {
        this.loadCompanyWifiConfig()
      },
      toggleWifiPassword () {
        this.showWifiPassword = !this.showWifiPassword
      },
      async loadCompanyDevices () {
        const companyId = this.companyId
        const requestToken = this.companyDeviceRequestToken + 1
        this.companyDeviceRequestToken = requestToken
        this.companyDeviceLoadError = ''

        if (!companyId) {
          this.companyDeviceRows = []
          this.companyDeviceLoadError = 'Select a company to reuse existing vehicle MAC addresses.'
          return
        }

        this.isLoadingCompanyDevices = true

        try {
          const pageLimit = 500
          const maxPages = 200
          const devices = []

          for (let page = 1; page <= maxPages; page += 1) {
            const response = await this.$http.get('Company/DevicePaginationByCompany', {
              params: { companyId, page, limit: pageLimit },
            })

            if (requestToken !== this.companyDeviceRequestToken) {
              return
            }

            const responseData = response?.data
            const pageDevices = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData?.data)
                ? responseData.data
                : []

            devices.push(...pageDevices)

            if (pageDevices.length < pageLimit) {
              break
            }
          }

          this.companyDeviceRows = devices
          this.applyVehicleMacForCurrentInput()
        } catch (error) {
          if (requestToken !== this.companyDeviceRequestToken) {
            return
          }

          this.companyDeviceRows = []
          this.companyDeviceLoadError =
            error?.response?.data?.message ||
            'We could not load company devices for vehicle suggestions.'
        } finally {
          if (requestToken === this.companyDeviceRequestToken) {
            this.isLoadingCompanyDevices = false
          }
        }
      },
      normalizeVehicleInput (value) {
        if (value && typeof value === 'object') {
          return getDeviceNameValue(value)
        }

        return String(value || '').trim()
      },
      // Resolve free text typed into the vehicle combobox to an existing
      // vehicle: exact match wins, then a prefix match, then a contains match.
      findMatchingVehicleOption (value) {
        const query = normalizeVehicleKey(value)
        if (!query) {
          return null
        }

        const exactMatch = this.vehicleNumberOptions.find(
          option => normalizeVehicleKey(option.name) === query,
        )
        if (exactMatch) {
          return exactMatch
        }

        const prefixMatch = this.vehicleNumberOptions.find(
          option => normalizeVehicleKey(option.name).startsWith(query),
        )
        if (prefixMatch) {
          return prefixMatch
        }

        return (
          this.vehicleNumberOptions.find(
            option => normalizeVehicleKey(option.name).includes(query),
          ) || null
        )
      },
      async applyVehicleMacForCurrentInput () {
        const typedVehicleNumber = this.normalizeVehicleInput(this.deviceImei)
        const matchedOption = this.findMatchingVehicleOption(typedVehicleNumber)
        const vehicleNumber = matchedOption ? matchedOption.name : typedVehicleNumber
        const vehicleKey = normalizeVehicleKey(vehicleNumber)

        if (this.deviceImei !== vehicleNumber) {
          this.deviceImei = vehicleNumber
        }

        if (!vehicleKey) {
          this.selectedExistingVehicleName = ''
          this.generatedVehicleName = ''
          return
        }

        const existingMacAddress = matchedOption
          ? matchedOption.macAddress
          : this.vehicleMacByName[vehicleKey]

        if (existingMacAddress) {
          this.deviceMacAddress = existingMacAddress
          this.selectedExistingVehicleName = vehicleNumber
          this.generatedVehicleName = ''
          await this.ensureQrProvisioningRecord()
          return
        }

        if (normalizeVehicleKey(this.generatedVehicleName) !== vehicleKey) {
          this.deviceMacAddress = generateUuidV7MacAddress()
          this.generatedVehicleName = vehicleNumber
        }

        this.selectedExistingVehicleName = ''
        await this.ensureQrProvisioningRecord()
      },
      async onVehicleNumberChanged (value) {
        this.deviceImei = this.normalizeVehicleInput(value)
        await this.applyVehicleMacForCurrentInput()
      },
      filterVehicleNumberOption (item, queryText, itemText) {
        const query = normalizeVehicleKey(queryText)
        if (!query) {
          return true
        }

        return normalizeVehicleKey(itemText || item?.name).includes(query)
      },
      async regenerateDeviceMacAddress () {
        const vehicleNumber = this.normalizeVehicleInput(this.deviceImei)
        const existingMacAddress = this.vehicleMacByName[normalizeVehicleKey(vehicleNumber)]

        if (existingMacAddress) {
          this.deviceMacAddress = existingMacAddress
          this.selectedExistingVehicleName = vehicleNumber
          this.generatedVehicleName = ''
          await this.ensureQrProvisioningRecord()
          return
        }

        this.deviceMacAddress = generateUuidV7MacAddress()
        this.generatedVehicleName = vehicleNumber
        this.selectedExistingVehicleName = ''
        await this.ensureQrProvisioningRecord()
      },
      async onDeviceMacAddressChanged () {
        await this.ensureQrProvisioningRecord()
      },
      describeApkDownloadError (error) {
        // Axios surfaces blocked cross-origin / mixed-content fetches as a bare
        // "Network Error", so spell out the likely cause for the operator.
        if (error?.message === 'Network Error' && !error?.response) {
          return 'Could not download the APK to calculate its checksum. The browser likely blocked the request (the Resources path may be missing CORS headers, or an http file was requested from an https page).'
        }

        return (
          error?.response?.data?.message ||
          error?.message ||
          'We could not download the APK to calculate its checksum.'
        )
      },
      async onApkUrlChanged () {
        const url = String(this.apkDownloadUrl || '').trim()
        this.apkDownloadUrl = url

        if (!url || !isValidHttpUrl(url)) {
          this.apkChecksum = ''
          this.checksumUrl = ''
          this.apkName = ''
          return
        }

        if (url === this.checksumUrl && this.apkChecksum) {
          return
        }

        const requestToken = this.apkRequestToken + 1
        this.apkRequestToken = requestToken
        this.apkError = ''
        this.apkChecksum = ''
        this.checksumUrl = ''
        this.apkName = getFileNameFromUrl(url) || 'the entered APK'
        this.isPreparingApk = true

        try {
          await this.resolveChecksumForUrl(url, requestToken)
        } catch (error) {
          if (requestToken !== this.apkRequestToken) {
            return
          }

          this.apkChecksum = ''
          this.checksumUrl = ''
          this.apkError = this.describeApkDownloadError(error)
        } finally {
          if (requestToken === this.apkRequestToken) {
            this.isPreparingApk = false
            this.isCalculatingChecksum = false
            this.isDownloadProgressKnown = false
            this.downloadProgress = 0
          }
        }
      },
      // The QR id is part of the admin extras bundle, so create the backend
      // record before rendering/exporting the final QR payload.
      async recordQrProvisioning () {
        const companyId = this.companyId
        const deviceName = this.vehicleNumber
        const deviceCode = this.macAddress

        if (!companyId || !deviceName || this.vehicleNumberError.length || !deviceCode) {
          return ''
        }

        const signature = this.qrProvisioningSignature
        if (signature === this.lastRecordedQrSignature && this.qrProvisioningId) {
          return this.qrProvisioningId
        }
        this.lastRecordedQrSignature = signature

        try {
          const response = await this.$http.post('QrProvisioning', {
            companyId,
            deviceName,
            deviceCode,
          })
          this.qrProvisioningId = response?.data?.data?.id || response?.data?.id || ''
          return this.qrProvisioningId
        } catch (error) {
          // Clear the cache so the next export can retry the recording.
          this.lastRecordedQrSignature = ''
          this.qrProvisioningId = ''
          console.error('[CompanyProvisioningQrDialog] Failed to record QR provisioning', {
            companyId,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
          return ''
        }
      },
      clearQrProvisioningRecord () {
        this.lastRecordedQrSignature = ''
        this.qrProvisioningId = ''
      },
      async ensureQrProvisioningRecord () {
        if (
          !this.companyId ||
          !this.vehicleNumber ||
          this.vehicleNumberError.length ||
          !this.macAddress
        ) {
          return false
        }

        const qrProvisioningId = await this.recordQrProvisioning()

        if (!qrProvisioningId) {
          this.$toast.error('Could not create the QR provisioning record.', 'Error', toastOptions)
        }

        return !!qrProvisioningId
      },
      async copyJson () {
        if (!this.canGenerate && !(await this.ensureQrProvisioningRecord())) {
          return
        }

        if (!this.canGenerate) {
          return
        }

        try {
          await copyTextToClipboard(this.formattedJson)
          this.$toast.success('Provisioning JSON copied to the clipboard.', 'Success', toastOptions)
        } catch (error) {
          this.$toast.error('We could not copy the JSON automatically.', 'Error', toastOptions)
        }
      },
      async downloadQrCode () {
        if (!this.canGenerate && !(await this.ensureQrProvisioningRecord())) {
          return
        }

        if (!this.canGenerate) {
          return
        }

        await this.$nextTick()

        const qrCanvas = this.$refs.qrWrapper ? this.$refs.qrWrapper.querySelector('canvas') : null

        if (!qrCanvas) {
          this.$toast.error('The QR code is not ready to download yet.', 'Error', toastOptions)
          return
        }

        const downloadLink = document.createElement('a')
        downloadLink.href = qrCanvas.toDataURL('image/png')
        downloadLink.download = `provisioning-qr-${this.companyCode || this.companyId}.png`
        downloadLink.click()
      },
    },
  }
</script>

<style scoped>
  .provisioning-qr-subtitle {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9rem;
  }

  .provisioning-qr-preview-col {
    display: flex;
  }

  .provisioning-qr-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    min-height: 500px;
    padding: 16px;
    border-radius: 8px;
    background: #fafafa;
  }

  .provisioning-qr-preview--empty {
    border: 1px dashed rgba(0, 0, 0, 0.12);
  }

  .wifi-section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .provisioning-qr-frame {
    padding: 20px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }

  .provisioning-qr-preview ::v-deep canvas {
    display: block;
    max-width: 100%;
    height: auto !important;
  }
</style>
